/**
 * Parse WKT CRS (PROJCS) to proj4 string for Lambert Conformal Conic 2SP and similar.
 * Used to convert WGS84 (lat/lon) to projected x,y (e.g. PA83-S state plane meters).
 */
import proj4 from 'proj4';
import { normalizeWktInput } from './wktNormalize';

const WGS84 = 'EPSG:4326';

/**
 * Extract numeric parameter from WKT string.
 * @param {string} wkt - Full WKT string
 * @param {string} paramName - e.g. "false_easting", "central_meridian"
 * @returns {number|null}
 */
function getWktParam(wkt, paramName) {
  if (!wkt || typeof wkt !== 'string') return null;
  const re = new RegExp(`PARAMETER\\["${paramName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\s*,\\s*([^\\]]+)\\]`, 'i');
  const m = wkt.match(re);
  if (!m) return null;
  const v = parseFloat(m[1].trim());
  return Number.isFinite(v) ? v : null;
}

/**
 * Detect projection type from WKT.
 * @param {string} wkt
 * @returns {string} proj name e.g. 'lcc', 'utm'
 */
function getProjectionType(wkt) {
  if (!wkt || typeof wkt !== 'string') return 'lcc';
  if (/Lambert_Conformal_Conic_2SP/i.test(wkt)) return 'lcc';
  if (/Transverse_Mercator/i.test(wkt)) return 'tmerc';
  return 'lcc';
}

/**
 * Build a proj4 definition string from WKT PROJCS (Lambert Conformal Conic 2SP supported).
 * @param {string} wkt - WKT CRS string (e.g. PROJCS["PA83-S", ...])
 * @returns {string|null} proj4 string or null if parsing fails
 */
export function wktToProj4String(wkt) {
  if (!wkt || typeof wkt !== 'string' || !wkt.trim()) return null;
  const proj = getProjectionType(wkt);
  if (proj === 'lcc') {
    const lat0 = getWktParam(wkt, 'latitude_of_origin');
    const lon0 = getWktParam(wkt, 'central_meridian');
    const lat1 = getWktParam(wkt, 'standard_parallel_1');
    const lat2 = getWktParam(wkt, 'standard_parallel_2');
    const x0 = getWktParam(wkt, 'false_easting');
    const y0 = getWktParam(wkt, 'false_northing');
    if (lat0 == null || lon0 == null) return null;
    let def = `+proj=lcc +lat_0=${lat0} +lon_0=${lon0}`;
    if (lat1 != null) def += ` +lat_1=${lat1}`;
    if (lat2 != null) def += ` +lat_2=${lat2}`;
    if (x0 != null) def += ` +x_0=${x0}`;
    if (y0 != null) def += ` +y_0=${y0}`;
    if (/NAD83|GRS1980/i.test(wkt)) def += ' +datum=NAD83';
    def += ' +units=m +no_defs';
    return def;
  }
  if (proj === 'tmerc') {
    const lat0 = getWktParam(wkt, 'latitude_of_origin');
    const lon0 = getWktParam(wkt, 'central_meridian');
    const x0 = getWktParam(wkt, 'false_easting');
    const y0 = getWktParam(wkt, 'false_northing');
    const k = getWktParam(wkt, 'scale_factor');
    if (lon0 == null) return null;
    let def = `+proj=tmerc +lat_0=${lat0 ?? 0} +lon_0=${lon0}`;
    if (x0 != null) def += ` +x_0=${x0}`;
    if (y0 != null) def += ` +y_0=${y0}`;
    if (k != null) def += ` +k=${k}`;
    if (/NAD83|GRS1980/i.test(wkt)) def += ' +datum=NAD83';
    def += ' +units=m +no_defs';
    return def;
  }
  return null;
}

/** Cache key for proj4.defs so server PROJ string and WKT-derived defs don't clash. */
let _cachedConverterKey = '';

/**
 * Get a proj4 converter between WGS84 and the given CRS.
 * Prefer options.proj4String from /api/wkt/validate (pyproj) when present — supports many more CRS than WKT scraping.
 * @param {string} wkt - WKT CRS string (fallback definition source)
 * @param {{ proj4String?: string|null }} [options]
 * @returns {{ forward: (lonLat: [number, number]) => [number, number] } | null}
 */
export function getWktToProj4Converter(wkt, options = {}) {
  const proj4Override = (options.proj4String && String(options.proj4String).trim()) || '';
  const wktKey = wkt && typeof wkt === 'string' ? normalizeWktInput(wkt) : '';
  if (!proj4Override && !wktKey) return null;

  const proj4Str = proj4Override || wktToProj4String(wktKey);
  if (!proj4Str) return null;

  const cacheKey = proj4Override ? `proj4:${proj4Override}` : `wkt:${wktKey}`;
  try {
    const key = 'CUSTOM_WKT_CRS';
    if (_cachedConverterKey !== cacheKey) {
      proj4.defs(key, proj4Str);
      _cachedConverterKey = cacheKey;
    }
    const converter = proj4(WGS84, key);
    return {
      forward(lonLat) {
        const [lon, lat] = lonLat;
        const out = converter.forward([lon, lat]);
        return [out[0], out[1]];
      },
      inverse(xy) {
        const [x, y] = xy;
        const out = converter.inverse([x, y]);
        return [out[0], out[1]]; // [lon, lat]
      },
    };
  } catch (e) {
    console.warn('wktCrsToProj4: getWktToProj4Converter failed', e);
    return null;
  }
}

/**
 * Convert an array of { lat, lng } points to [x, y, z] in the CRS defined by wkt.
 * z is set to 0 if not provided.
 * @param {Array<{ lat: number, lng: number }>} points - WGS84 points
 * @param {string} wkt - WKT CRS string
 * @param {number} [z=0] - Optional z value for all points
 * @param {{ proj4String?: string|null }} [options] - from validate response crs.proj4
 * @returns {Array<[number, number, number]>} Array of [x, y, z]
 */
export function latLngToProjectedXY(points, wkt, z = 0, options = {}) {
  const converter = getWktToProj4Converter(wkt, options);
  if (!converter || !Array.isArray(points)) return [];
  return points.map((p) => {
    const lat = Number(p.lat);
    const lng = Number(p.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [0, 0, z];
    const [x, y] = converter.forward([lng, lat]);
    return [x, y, z];
  });
}

/**
 * Convert a projected bbox (min_x, max_x, min_y, max_y) to WGS84 lat/lon bounds.
 * @param {{ min_x: number, max_x: number, min_y: number, max_y: number }} bbox - in projected CRS
 * @param {string} wkt - WKT CRS string
 * @param {{ proj4String?: string|null }} [options] - from validate response crs.proj4
 * @returns {{ south: number, north: number, west: number, east: number } | null}
 */
export function projectedBboxToLatLngBounds(bbox, wkt, options = {}) {
  const converter = getWktToProj4Converter(wkt, options);
  if (!converter || !bbox || typeof bbox.min_x !== 'number' || typeof bbox.max_x !== 'number' || typeof bbox.min_y !== 'number' || typeof bbox.max_y !== 'number') return null;
  const corners = [
    [bbox.min_x, bbox.min_y],
    [bbox.max_x, bbox.min_y],
    [bbox.max_x, bbox.max_y],
    [bbox.min_x, bbox.max_y],
  ];
  const lngLats = corners.map(([x, y]) => converter.inverse([x, y]));
  const lngs = lngLats.map(([lng]) => lng);
  const lats = lngLats.map(([, lat]) => lat);
  return {
    south: Math.min(...lats),
    north: Math.max(...lats),
    west: Math.min(...lngs),
    east: Math.max(...lngs),
  };
}
